import { useState } from 'react';
import { Divider, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BadgeIcon from '@mui/icons-material/Badge';
import KeyIcon from '@mui/icons-material/Key';
import LockResetIcon from '@mui/icons-material/LockReset';
import NaturePeopleIcon from '@mui/icons-material/NaturePeople';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';

import { useAuthContext } from '../../context/auth-context';
import { canChangeRole, canEditPermissions, canResetPassword } from '../../constants/roles';

/**
 * Acciones sobre un usuario de la tabla.
 *
 * Van en un menú y no en una fila de iconos porque son cinco y cada una aparece o no
 * según quién mire y a quién: con iconos sueltos, dos filas contiguas tendrían distinto
 * número de botones en distinto orden y no se sabría cuál es cuál.
 *
 * Lo que se pinta lo deciden los permisos efectivos, nunca el rol: un `grant` puntual
 * debe habilitar la acción sin tocar este archivo.
 */
export const UserActions = ({
  user,
  activeCampId,
  onChangeRole,
  onEditPermissions,
  onManageCamps,
  onResetPassword,
  onUnassignCamp,
}) => {
  const { user: actor, hasPermission } = useAuthContext();
  const [anchor, setAnchor] = useState(null);

  const cerrar = () => setAnchor(null);
  const lanzar = accion => () => {
    cerrar();
    accion(user);
  };

  const puedeGestionar = hasPermission('users.manage');
  const asignadoAlCampActivo =
    Boolean(activeCampId) &&
    (user?.camp || []).some(c => String(c?._id ?? c) === String(activeCampId));

  const acciones = [
    canChangeRole({ actor, hasPermission, target: user }) && {
      key: 'role',
      label: 'Cambiar rol',
      icon: <BadgeIcon fontSize="small" />,
      onClick: onChangeRole,
    },
    canEditPermissions({ hasPermission, target: user }) && {
      key: 'permissions',
      label: 'Editar permisos',
      icon: <KeyIcon fontSize="small" />,
      onClick: onEditPermissions,
    },
    puedeGestionar && {
      key: 'camps',
      label: 'Campamentos',
      icon: <NaturePeopleIcon fontSize="small" />,
      onClick: onManageCamps,
    },
    canResetPassword({ hasPermission, target: user }) && {
      key: 'reset',
      label: 'Resetear contraseña',
      icon: <LockResetIcon fontSize="small" />,
      onClick: onResetPassword,
    },
    puedeGestionar &&
      asignadoAlCampActivo && {
        key: 'unassign',
        label: 'Quitar de este campamento',
        icon: <PersonRemoveIcon fontSize="small" color="error" />,
        onClick: onUnassignCamp,
        peligrosa: true,
      },
  ].filter(Boolean);

  if (acciones.length === 0) return null;

  return (
    <>
      <Tooltip title="Acciones">
        <IconButton size="small" onClick={e => setAnchor(e.currentTarget)} aria-label={`Acciones de ${user?.username}`}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={cerrar}>
        {acciones.map(accion => [
          accion.peligrosa ? <Divider key={`${accion.key}-div`} /> : null,
          <MenuItem key={accion.key} onClick={lanzar(accion.onClick)}>
            <ListItemIcon>{accion.icon}</ListItemIcon>
            <ListItemText primaryTypographyProps={accion.peligrosa ? { color: 'error' } : undefined}>
              {accion.label}
            </ListItemText>
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
};

export default UserActions;
